// React-Screen CPersonalPurchaseMode ("Personalkaufmodus") - Strangler
// Phase 5, Klasse B. Runbook: tools/REACT-STRANGLER.md.
// Ersetzt die ESM-Huelle personalpurchasemode.js (aus
// lib/scripts/pos/components/CMP_CPersonalPurchaseMode.js).
// Legacy-DOM-Fixture: tests/react-fixtures/CPersonalPurchaseMode.html,
// gezogen aus tests/visual-baseline/27-overlay-personalkauf.html.
//
// Zahlungsart PPU: CPaymentProcessor setzt openSum/wholeSum und zeichnet den
// Screen, der dann auf die Personalkarte des KAUFENDEN Mitarbeiters wartet.
// Die Karte kommt ueber CBarcodeReader (displayMode "purchase" ->
// displayTarget.purchaserId + getPersonalCard()).
//
// Zwei Zustaende, und der zweite aendert die KIND-REIHENFOLGE: nach
// getPersonalCard() macht Legacy okButton.reDraw() - reDraw baut das Element
// ueber den Builder neu und haengt es ans ENDE des Elternknotens, hinter den
// backButton. Die JSX bildet das ab, indem der okButton je nach Zustand an
// Position 7 oder als letztes Kind gerendert wird (die ID kommt aus dem
// Pfad, nicht aus der Position).
import { defineReactScreen } from '../bridge.js';
import { CPushButton } from '../../components/pushbutton.js';

// Legacy-CCaption: innerHTML = value UND class = "CCaption <addclass> <value>".
function Caption({ bridge, path, addclass, value, style }) {
  const cls = 'CCaption' + (addclass ? ' ' + addclass : '') + ' ' + value;
  return <p {...bridge.el(path, 'CCaption')} className={cls} style={style}
            dangerouslySetInnerHTML={{ __html: value }} />;
}

function PersonalPurchaseModeScreen({ bridge }) {
  const t = bridge.t;
  // Der okButton bekommt erst mit gescannter Karte einen Handler
  // ("Bonieren"-Pendant: doPurchase); vorher ist er unsichtbar und passiv.
  const kaeuferDa = !!bridge.state.okAktiv;
  const okButton = (
    <div {...bridge.widget('7', 'CPushButton', kaeuferDa ? {
      events: { onMousedown: () => bridge.doPurchase(), onMouseout: true, onMouseup: true },
      attributes: { onMousedown_parameter_0: bridge.id },
      proto: CPushButton.prototype,
    } : {
      events: { onMousedown: true, onMouseout: true, onMouseup: true },
      proto: CPushButton.prototype,
    })} className="CPushButton okbutton"
        style={kaeuferDa ? undefined : { visibility: 'hidden' }}>
      <Caption bridge={bridge} path="7:0" addclass="" value=" " />
    </div>
  );
  const backButton = bridge.widget('8', 'CPushButton', {
    events: { onMousedown: () => bridge.cancel(), onMouseout: true, onMouseup: true },
    attributes: { onMousedown_parameter_0: bridge.id },
    proto: CPushButton.prototype,
  });

  return (
    <>
      {/* wholeOverlay */}
      <div {...bridge.el('0', 'CFrame')} className="CFrame wholeoverlay"
           style={{ position: 'absolute', visibility: 'visible' }}></div>
      {/* overlay */}
      <div {...bridge.el('1', 'CFrame')} className="CFrame overlay" style={{ position: 'absolute' }}></div>
      {/* titleBack */}
      <div {...bridge.el('2', 'CFrame')} className="CFrame titleback" style={{ position: 'absolute' }}></div>
      {/* titleText - enthaelt den Betrag samt Personalrabatt */}
      <Caption bridge={bridge} path="3" addclass="titletext" value={bridge.state.title} />
      {/* contentText */}
      <Caption bridge={bridge} path="4" addclass="contenttext" value={bridge.state.content}
               style={{ fontSize: '15px', top: '140px' }} />
      {/* priceText */}
      <Caption bridge={bridge} path="5" addclass="pricetext" value={bridge.state.price}
               style={{ top: '160px' }} />
      {/* unitsText */}
      <Caption bridge={bridge} path="6" addclass="unitstext" value=" " />
      {/* okButton steht vor dem Scannen hier ... */}
      {!kaeuferDa && okButton}
      {/* backButton */}
      <div {...backButton} className="CPushButton backbutton">
        <p {...bridge.el('8:0', 'CCaption')} className="CCaption undefined"></p>
      </div>
      {/* ... und nach dem reDraw() dahinter. */}
      {kaeuferDa && okButton}
    </>
  );
}

export const CPersonalPurchaseMode = defineReactScreen({
  className: 'CPersonalPurchaseMode',
  addclass: 'CPersonalPurchaseMode CashWindow',
  htmlType: 'div',
  Screen: PersonalPurchaseModeScreen,
  initialState: { title: '', content: '', price: ' ', okAktiv: false },
  prepare(bridge) {
    bridge.purchaserId = null;

    bridge.Main = POS.sessions.getSessionComponent();

    bridge.rebPercent = POS.EnvProperties['PERSONALPURCHASEPERCENT'];
    bridge.rebFactor = POS.EnvProperties['PERSONALPURCHASEPERCENT'] / 100;
    bridge.amFactor = 1 - POS.EnvProperties['PERSONALPURCHASEPERCENT'] / 100;

    bridge.state.title = bridge.t('LABELS', 'PERSONAL_PURCHASE_MODE') +
      "<br><br><span style='font-size:15px'>" + bridge.t('LABELS', 'PERSONAL_PURCHASE_AMOUNT') +
      '</span><br>' + float2StringD2(bridge.openSum) +
      ' &euro; - ' + bridge.rebPercent + "% = <span style='color:#EE0000'>" +
      float2StringD2(bridge.openSum * bridge.amFactor) + ' &euro;</span>';

    bridge.state.content = bridge.t('LABELS', 'PERSONAL_PURCHASE_MODE_TEXT');

    bridge.Main.BonManager.displayMode = 'purchase';
    bridge.Main.BonManager.displayTarget = bridge;

    bridge.Main.vacuumInfoValue(bridge.Main);

    bridge.Main.components.sumcaption.properties.styles.visibility = 'hidden';
    bridge.Main.components.sumcaption.reStyle();
    bridge.Main.components.suminfo.properties.styles.visibility = 'hidden';
    bridge.Main.components.suminfo.reStyle();
  },
  methods: {
    // Aufrufer: CBarcodeReader, wenn im Personalkaufmodus eine Personalkarte
    // gescannt wird (setzt vorher purchaserId).
    getPersonalCard(bridge) {
      // SQL-Text ZEICHENGENAU wie im Legacy (Master/Slave-Routing).
      var sql = 'SELECT U_ID FROM U WHERE Login = ' + bridge.purchaserId;
      $.when(POS.SQLiteCon.execute(sql)).then(function (rows) {
        if (rows.length < 1) {
          return;
        }

        if (rows[0].U_ID == POS.sessions.currentSession) {
          bridge.purchaserId = null;

          bridge.update({
            content: bridge.t('LABELS', 'PERSONAL_PURCHASE_MODE_TEXT'),
            price: bridge.purchaserId,
            okAktiv: false,
          });

          var text = bridge.t('MESSAGES', 'SAME_USER_PURCHASE');
          POS.message.show(text, 'warning', '');

          bridge.Main.components.info.input.setFocus();

          return;
        }

        bridge.update({
          content: bridge.t('LABELS', 'PURCHASER_PERSONAL_CARD'),
          price: bridge.purchaserId,
          okAktiv: true,
        });
      });
    },
    // OK: Personalrabatt buchen und bezahlen.
    doPurchase(bridge) {
      try {
        $.when(Trans.getPurchaserName(bridge.purchaserId)).then(function (res) {
          if (res.length > 0) {
            var realRebate = (bridge.openSum * bridge.rebFactor) / bridge.wholeSum;
            $.when(bridge.Main.BonManager.addEntryGRB(realRebate, true)).done(function () {
              $.when(bridge.Main.BonManager.payCallback(bridge.Main.BonManager, null,
                bridge.openSum * bridge.amFactor, 0, 0)).done(function () {
                bridge.finish();
              });
            });
          } else {
            log.add(1, 'Error', 'PPU user not in DB: ' + bridge.purchaserId);
            var text = bridge.t('MESSAGES', 'RESCAN_USER_PURCHASE');
            POS.message.show(text, 'warning', '');
          }
        }, function (error) {
          if (error) {
            log.add(1, 'Error', 'PPU user not in DB: ' + bridge.purchaserId);
            var text = bridge.t('MESSAGES', 'RESCAN_USER_PURCHASE');
            POS.message.show(text, 'warning', '');

            return;
          }
        });
      } catch (e) {
        log.add(1, 'Error', 'PPU user not in DB: ' + bridge.purchaserId);
        var text = bridge.t('MESSAGES', 'RESCAN_USER_PURCHASE');
        POS.message.show(text, 'warning', '');
        return;
      }
    },
    cancel(bridge) {
      bridge.Main.components.sumcaption.properties.styles.visibility = 'visible';
      bridge.Main.components.sumcaption.reStyle();
      bridge.Main.components.suminfo.properties.styles.visibility = 'visible';
      bridge.Main.components.suminfo.reStyle();

      bridge.finish();
    },
    finish(bridge) {
      bridge.Main.BonManager.displayMode = 'normal';
      bridge.Main.BonManager.displayTarget = null;

      bridge.Main.components.info.input.setFocus();

      log.add(4, 'Info', 'End personal purchase payment', '');
      bridge.removeMe();
    },
  },
});
